import { useEffect, useState } from 'react';

import { STATUSES, StepsEnum, salesMode } from 'commons';
import {
  CustomerAndPayment,
  DashboardHeader,
  DashboardSteps,
  Products,
} from 'components';
import { storedAttendance } from 'helpers';
import { useAuth } from 'hooks';
import {
  ConsignmentDetails as ConsignmentDetailsType,
  PointOfSaleType,
} from 'types';

import { DeliveryFailureAlert, Refuse } from './components';
import {
  StepConfirmEan,
  StepDelivered,
  StepInvoice,
  StepNone,
  StepTransport,
} from './components/steps';
import * as S from './styles';

interface Props {
  setSelectedConsignment: (consignment: null) => void;
  consignmentData: ConsignmentDetailsType;
  isError: boolean;
}

function getStep(consignmentData: ConsignmentDetailsType): StepsEnum {
  const { status, entries, code, lastRegistration, pointOfSale } =
    consignmentData;
  const {
    AVAILABLE_FOR_STORE,
    CANCELLED,
    RESERVED_BY_STORE,
    CONFIRMED_BY_STORE,
    READY_FOR_PICKUP_BY_DRIVER,
    PICKUP_BY_DRIVER,
    RELEASE_PAYMENT,
    CLARIFY_DELIVERY_FAIL,
    COLLECT_FAIL,
    READY_FOR_CUSTOMER_PICKUP_IN_STORE,
    DELIVERED,
    MANUALLY_DELIVERED,
    DELIVERED_BY_DRIVER,
    WAIT_FOR_INVOICE,
  } = STATUSES;

  const currentAttendance = storedAttendance.getCurrent({
    entries,
    consignmentCode: code,
  });
  const isInvoiceStep = currentAttendance.step === StepsEnum.Invoice;
  const isCollected = Boolean(lastRegistration.carrier);
  const isAutomatic = pointOfSale.type === PointOfSaleType.Automatic;

  switch (status) {
    case AVAILABLE_FOR_STORE.value:
    case RESERVED_BY_STORE.value:
      if (isInvoiceStep) {
        return StepsEnum.Invoice;
      } else {
        return StepsEnum.ConfirmEan;
      }

    case WAIT_FOR_INVOICE.value:
      return StepsEnum.Invoice;

    case CONFIRMED_BY_STORE.value:
    case RELEASE_PAYMENT.value:
      if (isAutomatic) {
        return StepsEnum.Invoice;
      } else {
        return StepsEnum.Transport;
      }

    case READY_FOR_PICKUP_BY_DRIVER.value:
    case PICKUP_BY_DRIVER.value:
    case CLARIFY_DELIVERY_FAIL.value:
    case COLLECT_FAIL.value:
      return StepsEnum.Transport;

    case READY_FOR_CUSTOMER_PICKUP_IN_STORE.value:
      if (isCollected) {
        return StepsEnum.Delivered;
      } else {
        return StepsEnum.Transport;
      }

    case DELIVERED.value:
    case MANUALLY_DELIVERED.value:
    case DELIVERED_BY_DRIVER.value:
    case CANCELLED.value:
      return StepsEnum.Delivered;

    default:
      return StepsEnum.None;
  }
}

export function ConsignmentDetails({
  setSelectedConsignment,
  consignmentData,
  isError,
}: Props) {
  const { selectedStore, user } = useAuth();
  const { isAdmin } = user;
  const [currentStep, setCurrentStep] = useState<StepsEnum>(
    getStep(consignmentData) || StepsEnum.ConfirmEan
  );
  const [showRefuse, setShowRefuse] = useState(false);

  const store = selectedStore.name || '';
  const { code, entries, logisticsFailureReason } = consignmentData;
  const isPickupInStore = consignmentData.salesMode === salesMode.pickup;
  const isConfirmEanStep = currentStep === StepsEnum.ConfirmEan;
  const isNoneStep = currentStep === StepsEnum.None;
  const isClarifyDeliveryFail =
    consignmentData.status === STATUSES.CLARIFY_DELIVERY_FAIL.value ||
    consignmentData.status === STATUSES.COLLECT_FAIL.value;

  useEffect(() => {
    const newStep = getStep(consignmentData);

    if (newStep !== currentStep) setCurrentStep(newStep);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consignmentData.status]);

  if (isError) {
    return (
      <S.CentralizedContainer>
        <span>Erro ao carregar informações</span>
      </S.CentralizedContainer>
    );
  }

  if (showRefuse && store) {
    return (
      <S.Main>
        <DashboardHeader consignment={consignmentData} />

        <S.Wrapper>
          <Refuse
            setSelectedConsignment={setSelectedConsignment}
            consignmentCode={code}
            store={store}
            entries={entries}
            setShowRefuse={setShowRefuse}
          />
        </S.Wrapper>
      </S.Main>
    );
  }

  const renderContainerSteps = () => {
    switch (currentStep) {
      case StepsEnum.ConfirmEan:
        return (
          <StepConfirmEan
            consignment={consignmentData}
            setCurrentStep={setCurrentStep}
            setShowRefuse={setShowRefuse}
          />
        );

      case StepsEnum.Invoice:
        return (
          <StepInvoice
            consignment={consignmentData}
            isPickupInStore={isPickupInStore}
            setShowRefuse={setShowRefuse}
          />
        );

      case StepsEnum.Transport:
        return (
          <StepTransport
            consignment={consignmentData}
            defaultOpen={!isClarifyDeliveryFail}
          />
        );

      case StepsEnum.Delivered:
        return <StepDelivered consignment={consignmentData} />;

      default:
        return <StepNone consignment={consignmentData} />;
    }
  };

  const renderDeliveryFailureAlert = () => {
    if (!isClarifyDeliveryFail) return null;

    const { isSuperExpress, pinCodes } = consignmentData;

    if (isSuperExpress) {
      return (
        <DeliveryFailureAlert
          isSuperExpress
          pinCode={pinCodes.superExpressReturnCode}
        />
      );
    }

    if (!logisticsFailureReason) return null;

    const { type, typeDescription, description } = logisticsFailureReason;

    return (
      <DeliveryFailureAlert
        type={type}
        title={typeDescription}
        description={description}
      />
    );
  };

  const renderOrderItems = () => {
    const isDeliveredStep = currentStep === StepsEnum.Delivered;
    const isConsignmentCancelled =
      consignmentData.status === STATUSES.CANCELLED.value;

    if (isConsignmentCancelled && !isAdmin) return null;

    if (isConfirmEanStep || isNoneStep) return null;

    return (
      <>
        <CustomerAndPayment
          consignment={consignmentData}
          hideCopyButton={isDeliveredStep}
          defaultOpen={!isClarifyDeliveryFail}
        />

        <Products
          entries={entries}
          hideCopyButton={isDeliveredStep}
          defaultOpen={!isClarifyDeliveryFail}
        />
      </>
    );
  };

  return (
    <S.Main>
      <DashboardHeader
        consignment={consignmentData}
        defaultOpen={isConfirmEanStep || isNoneStep}
      />

      <DashboardSteps
        currentStep={currentStep}
        isPickupInStore={isPickupInStore}
      />

      {renderDeliveryFailureAlert()}

      <S.Wrapper>
        {renderContainerSteps()}

        {renderOrderItems()}
      </S.Wrapper>
    </S.Main>
  );
}
